import { useMemo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import GroupIcon from "@mui/icons-material/Group";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import CategoryIcon from "@mui/icons-material/Category";
import { Link } from "react-router-dom";
import { MetricCard } from "../../Analytics/components/MetricCard";
import { UserLeaderboard } from "../../Analytics/components/UserLeaderboard";
import { CategoryRadar } from "../../Analytics/components/CategoryRadar";
import { SectionCard } from "../../../components/common/SectionCard";
import { EmptyState } from "../../../components/common/EmptyState";
import { useGetUserMetrics } from "../../../api/generated/generated";
import type {
  DashboardSummary,
  UserMetricsResponse,
} from "../../../api/generated";

interface TeamOverviewProps {
  summary: DashboardSummary | undefined;
}

export function TeamOverview({ summary }: TeamOverviewProps) {
  const { data: userMetricsRes } = useGetUserMetrics(undefined, {
    query: { enabled: true },
  });
  const userMetrics = userMetricsRes?.data as UserMetricsResponse | undefined;

  const topUsers = useMemo(() => {
    if (!userMetrics?.users) return [];
    return [...userMetrics.users]
      .sort((a, b) => (b.avg_score ?? -1) - (a.avg_score ?? -1))
      .slice(0, 5);
  }, [userMetrics]);

  const conversations = summary?.conversations;
  const scores = summary?.scores;
  const activity = summary?.activity;

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 3 }} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5">Team Overview</Typography>
        <Button component={Link} to="/analytics" size="small">
          View all in Analytics
        </Button>
      </Box>

      {/* Team Metric Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            title="Total Conversations"
            value={conversations?.total_count ?? 0}
            change={conversations?.change_percent}
            icon={<GroupIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            title="Total Analyses"
            value={conversations?.post_call_count ?? 0}
            icon={<AssessmentIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            title="Team Avg Score"
            value={scores?.avg_overall ?? null}
            change={scores?.change_percent}
            icon={<TrendingUpIcon />}
            format="score"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            title="Active Users"
            value={activity?.active_users ?? 0}
            icon={<PeopleIcon />}
          />
        </Grid>
      </Grid>

      {/* Leaderboard + Category Radar */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <SectionCard title="Top Performers">
            {topUsers.length > 0 ? (
              <UserLeaderboard users={topUsers} />
            ) : (
              <EmptyState
                icon={<PeopleIcon />}
                title="No user data yet"
                description="Performance data will appear after team members complete call analyses."
              />
            )}
          </SectionCard>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <SectionCard title="Score by Category" sx={{ height: "100%" }}>
            {scores?.avg_by_category &&
            Object.keys(scores.avg_by_category).length > 0 ? (
              <CategoryRadar data={scores.avg_by_category} />
            ) : (
              <EmptyState
                icon={<CategoryIcon />}
                title="No category data yet"
                description="Category scores will appear after call analyses are completed."
              />
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
